// src/features/map/layers/AircraftOverlay.jsx
// Polls /api/layers/aircraft while the layer is toggled on and the tab is
// visible; pushes GeoJSON into MapView's aircraft source; renders the
// click popup. Mirrors the RiskmizeMapOverlay mount pattern.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Plane, X } from 'lucide-react';
import { layers as layersApi } from '@/lib/trackizy';
import useMapLayerPrefs from '@/hooks/useMapLayerPrefs';
import useFeatures from '@/hooks/useFeatures';
import AircraftPanel from './AircraftPanel';

const POLL_MS = 10_000;

// MapLibre GeoJSON properties pass through vector-tile-style serialization —
// a JS `null` value can arrive as the string `"null"` (or be dropped
// entirely) rather than surviving as `null`. Normalize once here, at the
// source, so AircraftPanel can treat every falsy/'null' field as absent.
function val(x) { return x == null || x === 'null' ? null : x; }

export default function AircraftOverlay({ mapViewRef, premium = false }) {
  const { t } = useTranslation();
  const { layers: prefs } = useMapLayerPrefs();
  const features = useFeatures();
  const active = prefs.aircraft && features.aircraftLayer;
  const [popup, setPopup] = useState(null); // { properties, anchor }
  const [panelFlight, setPanelFlight] = useState(null); // premium flight detail panel

  // Poll + push. On fetch error keep the last pushed data (soft degrade).
  useEffect(() => {
    mapViewRef.current?.setAircraftVisible?.(active);
    if (!active) {
      setPopup(null);
      setPanelFlight(null);
      return undefined;
    }
    let stopped = false;
    const tick = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const fc = await layersApi.aircraft();
        if (!stopped) mapViewRef.current?.setAircraftData?.(fc);
      } catch { /* keep last data — never a broken map */ }
    };
    tick();
    const timer = setInterval(tick, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [active, mapViewRef]);

  // Click → popup, or (premium + FR24-sourced feature) → detail panel.
  // MapView forwards hits as container CustomEvents.
  useEffect(() => {
    const map = mapViewRef.current?.getMap?.();
    const container = map?.getContainer?.();
    if (!container) return undefined;
    const onClick = (e) => {
      const detail = e.detail || null;
      const properties = detail?.properties || {};
      if (premium && properties.fr24Id) {
        const normalized = Object.fromEntries(Object.entries(properties).map(([k, v]) => [k, val(v)]));
        setPanelFlight({ ...normalized, __lat: detail.lngLat[1], __lon: detail.lngLat[0] });
        setPopup(null);
      } else {
        // Non-FR24 click (e.g. the adsb.fi fallback feed, no fr24Id) — the
        // popup takes over; drop any stale premium panel + its trail too.
        setPanelFlight(null);
        setPopup(detail);
      }
    };
    const onMove = () => setPopup(null);
    container.addEventListener('trackizy:aircraft-click', onClick);
    map.on('movestart', onMove);
    return () => {
      container.removeEventListener('trackizy:aircraft-click', onClick);
      map.off('movestart', onMove);
    };
  }, [mapViewRef, premium]);

  if (!popup && !panelFlight) return null;
  const p = popup?.properties || {};
  // Anchor is in map-CONTAINER pixel coordinates; translate to viewport
  // coordinates via the container's bounding rect so the popup lands under
  // the cursor regardless of where the map sits on the page.
  const rect = popup ? mapViewRef.current?.getMap?.()?.getContainer?.()?.getBoundingClientRect?.() : null;
  if (popup && !rect) return null;
  return createPortal(
    <>
      {popup && rect && (
        <div
          className="fixed z-50 rounded-xl shadow-xl p-3 min-w-[160px] text-xs"
          style={{
            left: rect.left + popup.anchor.x + 8, top: rect.top + popup.anchor.y + 8,
            background: 'var(--glass-bg)', backdropFilter: 'blur(12px)',
            border: '1px solid var(--glass-border)',
          }}
        >
          <div className="flex items-center gap-2 mb-1.5">
            <Plane size={13} className="text-sky-500" />
            <span className="font-semibold text-slate-800 dark:text-slate-100"><bdi>{p.callsign || p.icao || '—'}</bdi></span>
            <button type="button" onClick={() => setPopup(null)} className="ms-auto text-slate-400 hover:text-slate-600"><X size={12} /></button>
          </div>
          <div className="space-y-0.5 text-slate-600 dark:text-slate-300">
            <div>{t('mapLayers.altitude', 'Altitude')}: <bdi>{p.altFt != null ? `${Number(p.altFt).toLocaleString()} ft` : '—'}</bdi></div>
            <div>{t('mapLayers.speed', 'Speed')}: <bdi>{p.speedKt != null ? `${p.speedKt} kt` : '—'}</bdi></div>
          </div>
        </div>
      )}
      {panelFlight && (
        <AircraftPanel flight={panelFlight} mapViewRef={mapViewRef} onClose={() => setPanelFlight(null)} />
      )}
    </>,
    document.body,
  );
}
